import { useEffect, useState } from "react";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import Icon from "../../../../components/base/Icon";
import Indicator from "../../../../components/base/Indicator";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import { useHub } from "../../../../core/Global";
import PaymentMethod from "../models/PaymentMethod";
import PaymentCardView from "./PaymentCardView";
import NewCardOption from "./NewCardOption";


const NEW_CARD = "__new_card__"

interface IProps {
    amountText?: string
    onClose?: () => void
    onSaved?: () => void
}

export default function SubscriptionPayDrawer(props: IProps) {

    const hub = useHub()

    const [methods, methodsSet] = useState<PaymentMethod[]>([])
    const [selectedMethodId, selectedMethodIdSet] = useState<string>()
    const [fetching, fetchingSet] = useState<boolean>(true)

    useEffect(() => {
        hub.Get("/accounts/profile/paymentmethods/", res => {
            const data: PaymentMethod[] = res?.data ?? []
            methodsSet(data)
            const primary = data.find(m => m.isPrimary) ?? data[0]
            selectedMethodIdSet(primary?.id ?? NEW_CARD)
            fetchingSet(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <DrawerForm id="drawer-subscription-pay"
            title="Subscribe"
            size="medium"
            buttonText={props.amountText ? `Pay ${props.amountText}` : "Subscribe"}
            onClose={props.onClose}
            onSubmitted={props.onSaved}
            onSubmit={(body, callback) => {

                if (!selectedMethodId) {
                    toastError("Please choose a payment method.")
                    callback!()
                    return
                }

                // New card: send the user to Stripe hosted Checkout to enter card details and pay.
                if (selectedMethodId === NEW_CARD) {
                    hub.Post("/accounts/profile/subscription/pay/newcard", {}, res => {
                        if (res.success && res.data?.url) {
                            document.location.href = res.data.url
                            return
                        }
                        toastError(res.message ?? "Couldn't start the payment.")
                        callback!(res)
                    })
                    return
                }

                hub.Post("/accounts/profile/subscription/pay", {
                    paymentMethodId: selectedMethodId
                }, res => {
                    // Card needs 3-D Secure: send the user to Stripe to authenticate. They are
                    // redirected back to the subscription page once the charge is finalized.
                    if (res.success && res.data?.requiresAction && res.data?.redirectUrl) {
                        document.location.href = res.data.redirectUrl
                        return
                    }

                    if (res.success) toastSuccess("Your subscription is now active!")
                    else toastError(res.message ?? "Payment failed!")
                    callback!(res)
                })
            }}>

            <div className="mb-8">
                <label className="fs-6 fw-bold text-gray-800 mb-3 d-block">Plan</label>
                <div className="d-flex align-items-center justify-content-between p-4 rounded bg-light-primary">
                    <span className="fw-bold text-gray-800">Monthly Subscription</span>
                    <span className="fs-4 fw-bolder text-gray-900">{props.amountText}</span>
                </div>
            </div>

            <div className="mb-4">
                <label className="fs-6 fw-bold text-gray-800 mb-3 d-block">Payment Method</label>

                {
                    fetching ?
                        <div className="text-center py-8">
                            <Indicator />
                        </div>
                        :
                        <div className="d-flex flex-column gap-3">

                            {
                                methods.map(m => {
                                    const selected = selectedMethodId === m.id
                                    return (
                                        <div key={m.id}
                                            className={`d-flex align-items-center p-4 rounded border cursor-pointer ${selected ? "border-primary bg-light-primary" : "border-gray-300"}`}
                                            onClick={() => selectedMethodIdSet(m.id)}>
                                            <PaymentCardView item={m} />
                                            <div className="ms-auto">
                                                <Icon
                                                    icon={selected ? "circle-check" : "circle"}
                                                    type="solid"
                                                    size={20}
                                                    className={selected ? "text-primary" : "text-gray-400"} />
                                            </div>
                                        </div>
                                    )
                                })
                            }

                            <NewCardOption
                                selected={selectedMethodId === NEW_CARD}
                                onClick={() => selectedMethodIdSet(NEW_CARD)} />
                        </div>
                }
            </div>

        </DrawerForm>
    )
}
